/**
 * Where a word starts and stops — the one definition, shared by everything that
 * has an opinion about it.
 *
 * Three surfaces need to agree: the web converter that turns an ElevenLabs
 * character alignment into word timings, the backend converter that does the
 * same inside the story pipeline, and the Whisper re-aligner that tokenizes the
 * story text directly. They each grew their own copy and one drifted — the web
 * copy tested characters against `\w`, which is ASCII-only, so Italian "Perché"
 * was cut short at "Perch" and a word like "è" disappeared from the timings
 * altogether. The iOS player renders these strings verbatim, so the reader saw
 * the damage. Anything that needs to split a word imports from here.
 */
/**
 * A word is a run of non-whitespace characters. Accents are letters and
 * punctuation rides along with the word it is attached to ("bellissima,"),
 * because these strings are displayed to a reader, not just matched.
 */
const WHITESPACE = /\s/;
/** Split source text into words, keeping each word's offset into the text. */
export function tokenizeWords(text) {
    const words = [];
    const re = /\S+/g;
    let match;
    while ((match = re.exec(text)) !== null) {
        words.push({ word: match[0], charIndex: match.index });
    }
    return words;
}
/**
 * Fold ElevenLabs' character-level alignment up into word-level timings. A word
 * runs from the start of its first character to the end of its last.
 */
export function segmentAlignment(alignment) {
    const { characters, character_start_times_seconds: starts, character_end_times_seconds: ends, } = alignment;
    const words = [];
    let word = "";
    let startTime = 0;
    let charIndex = 0;
    const flush = (lastCharIndex) => {
        if (!word)
            return;
        words.push({
            word,
            startTime,
            endTime: ends[lastCharIndex] ?? startTime,
            charIndex,
        });
        word = "";
    };
    for (let i = 0; i < characters.length; i++) {
        if (WHITESPACE.test(characters[i])) {
            flush(i - 1);
            continue;
        }
        if (!word) {
            startTime = starts[i] ?? 0;
            charIndex = i;
        }
        word += characters[i];
    }
    flush(characters.length - 1);
    return words;
}
/** Total spoken duration of an alignment, in seconds. */
export function alignmentDuration(alignment) {
    const ends = alignment.character_end_times_seconds;
    return ends[ends.length - 1] ?? 0;
}
/**
 * Fold a word down to a comparison key, for matching a word in the story text
 * against a word in the timings when the two disagree about surface form.
 *
 * Diacritics are stripped so a mishearing ("perche" from Whisper) still matches
 * the source ("perché"), and punctuation is dropped so "bellissima," matches
 * "bellissima". The letter class must be Unicode-aware: with an ASCII `\w`, the
 * Italian word "è" folds to the empty string and callers that skip empty keys as
 * "pure punctuation" silently drop the word.
 */
export function normalizeWord(word) {
    return word
        .toLowerCase()
        .normalize("NFKD")
        .replace(/\p{M}+/gu, "")
        .replace(/[^\p{L}\p{N}]+/gu, "");
}
//# sourceMappingURL=alignment.js.map